// Frontend client for the Track A segmentation API (/api/v1/segmentation/*).
// Replaces the Swagger UI: launch a run per platform, watch its progress, and
// download the TQ "DemandSensing" workbook. Every call carries the admin code.
//
// Every call also carries the TENANT. The server resolves it from the Host
// header, which is right in production — the two deployments are two hostnames
// — but a local `?tenant=wonderland` override lives in the query string of the PAGE,
// not of the API call, and `localhost` resolves to the client. Without this
// header the demo's Segmentation workspace would read the client's real scraped
// corpus, which is the exact leak the whole tenanting exists to prevent.

import type { DestinationIntent } from "@ow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardAdminError } from "./dashboard-api";
import { tenantHeaders } from "./tenant";

// Re-exported so segmentation components can `instanceof`-narrow errors without
// reaching into dashboard-api directly.
export { DashboardAdminError } from "./dashboard-api";

export type Platform = "reddit" | "twitter" | "instagram" | "tripadvisor";

export interface SegExport {
  id: string;
  kind: string;
  templateVersion: string;
  storageUri?: string | null;
  byteSize: number | null;
  generatedAt: string | null;
  downloadUrl: string;
}

/** A run's spend: LLM scoring + vendor/API scraping, plus the budget cap. */
export interface RunCost {
  llmUsd: number;
  vendorUsd: number;
  totalUsd: number;
  capUsd: number;
}

export interface SegRun {
  id: string;
  platform: Platform;
  name: string;
  status: string; // running | completed | failed | draft
  lastStage?: string | null; // discover | enrich | classify | segment | export
  lastStageAt?: string | null;
  running?: boolean;
  filters?: Record<string, unknown> | null;
  createdAt: string;
  completedAt?: string | null;
  cost?: RunCost | null;
  export?: SegExport | null;
}

export interface RunProgress {
  discovered: number;
  fetched: number;
  genuine: number;
  segmented: number;
}

export interface RunDetailResponse {
  run: SegRun;
  progress: RunProgress;
}

export interface Traveler {
  profileId: string;
  platform: Platform;
  handle: string | null;
  displayName: string | null;
  profileUrl: string | null;
  bio: string | null;
  followers: number | null;
  followersBucket: string | null;
  verified: boolean | null;
  accountType: string | null;
  language: string | null;
  countryIso3: string | null;
  isGenuine: boolean | null;
  reasons: string[] | null;
  decidedBy: string | null;
  decidedAt: string | null;
  segment: TravelerSegment | null;
}

export interface TravelerSegment {
  segment: string;
  confidence: number;
  secondChoice: string | null;
  faithSubtype: string | null;
  whySegment: string | null;
  headlineQuotes: string[] | null;
}

async function authError(path: string, res: Response) {
  const payload = await res.json().catch(() => null);
  return new DashboardAdminError(path, res.status, payload);
}

async function authGet<T>(path: string, accessCode: string | null): Promise<T> {
  // Public reads (run list, explorer) pass no code; gated reads pass one. Omit
  // the CODE entirely when there's none so a public endpoint isn't handed a
  // bogus "null" credential — but the tenant goes on every request either way.
  const res = await fetch(path, {
    headers: { ...tenantHeaders(), ...(accessCode ? { "x-admin-access-code": accessCode } : {}) },
  });
  if (!res.ok) throw await authError(path, res);
  return (await res.json()) as T;
}

async function authPost<T>(path: string, accessCode: string): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "x-admin-access-code": accessCode, ...tenantHeaders() },
  });
  if (!res.ok) throw await authError(path, res);
  return (await res.json()) as T;
}

async function authBodyJson<T>(method: "POST" | "PUT", path: string, accessCode: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: { "x-admin-access-code": accessCode, "content-type": "application/json", ...tenantHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await authError(path, res);
  return (await res.json()) as T;
}

const anyActive = (runs: SegRun[]) => runs.some((r) => r.status === "running" || r.running);

/** List every run, newest first. Polls while any run is still in flight. */
export function useRuns(accessCode: string | null, opts: { platform?: Platform; status?: string } = {}) {
  const params = new URLSearchParams({ limit: "100" });
  if (opts.platform) params.set("platform", opts.platform);
  if (opts.status) params.set("status", opts.status);
  return useQuery({
    queryKey: ["seg", "runs", opts.platform ?? "all", opts.status ?? "all"],
    // Public read — the run list loads with or without a code (the dashboard's
    // Segmentation workspace opens it codeless; the console passes its code).
    refetchInterval: (query) => {
      const runs = (query.state.data as { runs: SegRun[] } | undefined)?.runs ?? [];
      return anyActive(runs) ? 3000 : false;
    },
    queryFn: () => authGet<{ runs: SegRun[] }>(`/api/v1/segmentation/runs?${params}`, accessCode),
  });
}

/** One run's live status + stage progress. Polls while still running. */
export function useRunDetail(accessCode: string | null, runId: string | null) {
  return useQuery({
    queryKey: ["seg", "run", runId],
    enabled: Boolean(accessCode && runId),
    refetchInterval: (query) => {
      const run = (query.state.data as RunDetailResponse | undefined)?.run;
      return run && (run.status === "running" || run.running) ? 2500 : false;
    },
    queryFn: () => authGet<RunDetailResponse>(`/api/v1/segmentation/runs/${runId}`, accessCode!),
  });
}

/** Genuine travelers (or all assessed) for a run — loaded on demand. */
export function useTravelers(accessCode: string | null, runId: string | null, all: boolean, enabled: boolean) {
  return useQuery({
    queryKey: ["seg", "travelers", runId, all],
    enabled: Boolean(accessCode && runId && enabled),
    queryFn: () =>
      authGet<{ count: number; travelers: Traveler[] }>(
        `/api/v1/segmentation/runs/${runId}/travelers${all ? "?all=true" : ""}`,
        accessCode!,
      ),
  });
}

/* Explorer payload — every genuine traveler in a run folded into the trait
   model the /segmentation-ui Explorer consumes (mirrors the server's
   buildExplorerPayload). */
export interface ExplorerTrait {
  v: string;
  p: string;
  c: number | null;
}
export interface ExplorerProfileDTO {
  id: string;
  platform: Platform;
  handle: string | null;
  name: string | null;
  verified: boolean;
  followers: string | null;
  posts: number;
  lang: string | null;
  demo: Record<string, ExplorerTrait>;
  beh: Record<string, ExplorerTrait>;
  /** `vop` is the SHARED intent verdict, already folded server-side by
   *  `destinationIntent` — the web never re-reads the raw `visited_or_planned`
   *  label. Note the staleness case the header comment above describes: against
   *  a backend deployed before that fold, every row arrives as `"visited"`
   *  (the old mapping tested for a `"wishlist"` spelling production never
   *  wrote), so the wishlist tab reads empty until the server ships too. They
   *  deploy together, so this only bites local dev proxying at old prod. */
  dests: { cc: string | null; city: string | null; vop: DestinationIntent; y: number | null; url: string | null }[];
  saudi: { mention: boolean; count: number; visited: boolean; sentiment: string | null };
  segment: string | null;
  why: string | null;
  quotes: string[];
}
export interface ExplorerPayload {
  agg: {
    /**
     * Profiles scraped in (top of funnel); `total` is the genuine subset of it.
     * OPTIONAL on purpose: a backend older than the deploy that added these does
     * not send them, and in local dev Vite proxies /api at the DEPLOYED backend
     * (vite.config.ts), so callers MUST handle them being absent rather than
     * rendering a blank. Present together or not at all.
     */
    ingested?: number;
    /** `ingested` split by platform — sums to `ingested`, unlike `by_platform`. */
    ingested_by_platform?: Record<string, number>;
    total: number;
    by_platform: Record<string, number>;
    by_lang: Record<string, number>;
    saudi_mention_count: number;
    saudi_visited_count: number;
    total_signals: number;
  };
  profiles: ExplorerProfileDTO[];
  /** Present on the combined payload: the run that fed each platform. */
  sources?: { platform: Platform; cohortId: string; name: string; completedAt: string | null }[];
}

/** Sentinel run id for the combined "all platforms (latest)" view. */
export const COMBINED_RUN_ID = "combined";

/** localStorage key holding the admin access code (shared with /admin). */
export const ADMIN_CODE_KEY = "ow-admin-access-code";

/** Full cohort folded into the Explorer trait model. `runId === COMBINED_RUN_ID`
 *  unions the latest completed run of every platform; otherwise it's one run. */
export const explorerQuery = (accessCode: string | null, runId: string | null) => ({
  queryKey: ["seg", "explorer", runId] as const,
  staleTime: 5 * 60_000,
  queryFn: () =>
    authGet<ExplorerPayload>(
      runId === COMBINED_RUN_ID
        ? "/api/v1/segmentation/runs/combined/explorer"
        : `/api/v1/segmentation/runs/${runId}/explorer`,
      accessCode,
    ),
});

export function useExplorer(accessCode: string | null, runId: string | null) {
  return useQuery({
    ...explorerQuery(accessCode, runId),
    // Public read — explorer payloads load codeless for the dashboard workspace.
    enabled: Boolean(runId),
  });
}

export interface LaunchInput {
  platform: Platform;
  /** Pre-built query string (without leading "?"). */
  query: string;
}

/** Launch a run. Async (202) by default; sync (200) when query carries wait=true. */
export function useLaunchRun(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ platform, query }: LaunchInput) =>
      authPost<{ run: SegRun; export?: SegExport | null }>(
        `/api/v1/segmentation/${platform}/runs${query ? `?${query}` : ""}`,
        accessCode!,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seg", "runs"] }),
  });
}

/* Editable workbook — the DemandSensing "Profiles" sheet as a grid the reviewer
   can edit/delete cell-by-cell, save, and download. */
export type WorkbookCell = string | number | boolean | null;
export interface WorkbookResponse {
  sheet: string;
  platform: Platform;
  /** true once a saved edit overlay exists; false when freshly generated. */
  edited: boolean;
  updatedAt: string | null;
  headers: string[];
  rows: WorkbookCell[][];
  rowCount: number;
  colCount: number;
}
export interface WorkbookSaveResult {
  saved: boolean;
  updatedAt: string;
  rowCount: number;
  colCount: number;
}

/** The workbook grid for a run — the saved overlay if any, else generated. */
export function useWorkbook(accessCode: string | null, runId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["seg", "workbook", runId],
    enabled: Boolean(accessCode && runId && enabled),
    queryFn: () => authGet<WorkbookResponse>(`/api/v1/segmentation/runs/${runId}/workbook`, accessCode!),
  });
}

/** Save the whole edited grid (headers + rows). Refreshes the workbook query. */
export function useSaveWorkbook(accessCode: string | null, runId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grid: { headers: string[]; rows: WorkbookCell[][] }) =>
      authBodyJson<WorkbookSaveResult>("PUT", `/api/v1/segmentation/runs/${runId}/workbook`, accessCode!, grid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seg", "workbook", runId] }),
  });
}

/** Discard edits and revert to the generated workbook. Returns the fresh grid. */
export function useResetWorkbook(accessCode: string | null, runId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authPost<WorkbookResponse>(`/api/v1/segmentation/runs/${runId}/workbook/reset`, accessCode!),
    onSuccess: (data) => qc.setQueryData(["seg", "workbook", runId], data),
  });
}

/** Stream the current (saved-or-generated) workbook as .xlsx and download it. */
export async function downloadWorkbook(runId: string, accessCode: string, fallbackName: string): Promise<void> {
  const path = `/api/v1/segmentation/runs/${runId}/workbook/download`;
  const res = await fetch(path, { headers: { "x-admin-access-code": accessCode, ...tenantHeaders() } });
  if (!res.ok) throw await authError(path, res);
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = match?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Rebuild the workbook from already-extracted facts (no scraping / no LLM). */
export function useRebuildExport(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      authPost<{ export: SegExport }>(`/api/v1/segmentation/runs/${runId}/export`, accessCode!),
    onSuccess: (_d, runId) => {
      qc.invalidateQueries({ queryKey: ["seg", "runs"] });
      qc.invalidateQueries({ queryKey: ["seg", "run", runId] });
    },
  });
}

/** Stream the workbook (auth header required) and trigger a browser download. */
export async function downloadExport(runId: string, accessCode: string, fallbackName: string): Promise<void> {
  const path = `/api/v1/segmentation/runs/${runId}/export`;
  const res = await fetch(path, { headers: { "x-admin-access-code": accessCode, ...tenantHeaders() } });
  if (!res.ok) throw await authError(path, res);
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = match?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
